import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Code2, Copy, Check } from "lucide-react"
import Modal from "../Modal"

const LOCALES = [
  { code: "ro", label: "Română" },
  { code: "ru", label: "Русский" },
  { code: "uk", label: "Українська" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "bg", label: "Български" },
]

// "uk-UA" -> "uk"; неизвестный язык -> "en"
const normalizeLocale = (lng) => {
  const short = String(lng || "").split("-")[0]
  return LOCALES.some((l) => l.code === short) ? short : "en"
}

// "my-block" -> "#my-block"; ".cls" и "#id" оставляем; убираем кавычки и пробелы
const normalizeTarget = (value) => {
  const v = String(value || "")
    .replace(/["'<>\s]/g, "")
    .trim()
  if (!v) return ""
  return /^[#.]/.test(v) ? v : `#${v}`
}

const buildSnippet = ({ origin, locale, themeName, target }) => {
  const attrs = [`data-locale="${locale}"`, `data-theme="${themeName}"`]
  if (target) attrs.unshift(`data-target="${target}"`)

  const script = `<script src="${origin}/embed/v1/avia-search.js"\n        ${attrs.join("\n        ")}></script>`

  // для id-селектора показываем и сам блок
  if (target && target.startsWith("#")) {
    return `<div id="${target.slice(1)}"></div>\n${script}`
  }
  return script
}

const EmbedCodeButton = ({ themeName = "default", className = "" }) => {
  const { t, i18n } = useTranslation()
  const siteLocale = normalizeLocale(i18n.language)

  const [isOpen, setIsOpen] = useState(false)
  const [manualLocale, setManualLocale] = useState(null)
  const [targetInput, setTargetInput] = useState("")
  const [copied, setCopied] = useState(false)

  // при смене языка на сайте сбрасываем ручной выбор, код следует за сайтом
  useEffect(() => {
    setManualLocale(null)
  }, [siteLocale])

  const locale = manualLocale ?? siteLocale
  const target = normalizeTarget(targetInput)

  const snippet = useMemo(
    () => buildSnippet({ origin: window.location.origin, locale, themeName, target }),
    [locale, themeName, target]
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet)
    } catch (e) {
      const area = document.createElement("textarea")
      area.value = snippet
      document.body.appendChild(area)
      area.select()
      document.execCommand("copy")
      document.body.removeChild(area)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const title = t("admin.embed_code", { defaultValue: "Embed code" })

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        title={title}
        aria-label={title}
        className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md bg-white text-app-base-strong shadow-sm transition hover:bg-app-base/10 ${className}`}
      >
        <Code2 size={18} />
      </button>

      <Modal isOpen={isOpen} title={title} onClose={() => setIsOpen(false)}>
        <div className="flex w-full flex-col gap-5 sm:w-[560px] sm:max-w-full">
          <p className="text-sm text-app-content-subtle">
            {t("admin.embed_hint", {
              defaultValue: "Paste this code into any website where the search form should appear.",
            })}
          </p>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-app-content-subtle">
              {t("admin.language", { defaultValue: "Language" })}
            </span>
            <div className="flex flex-wrap gap-2">
              {LOCALES.map((l) => {
                const active = l.code === locale
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setManualLocale(l.code)}
                    className={`h-8 rounded-full border px-3 text-sm transition ${
                      active
                        ? "border-app-base-strong bg-app-base-strong font-semibold text-white"
                        : "border-app-content-faint/40 bg-white text-app-content hover:border-app-base-strong hover:text-app-base-strong"
                    }`}
                  >
                    {l.label}
                  </button>
                )
              })}
            </div>
          </div>

          <label className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-app-content-subtle">
              {t("admin.embed_target", { defaultValue: "Target block" })}
            </span>
            <input
              type="text"
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              placeholder="#my-block"
              spellCheck={false}
              className="h-10 w-full rounded-input border border-app-content-faint/40 bg-white px-3 font-mono text-sm text-app-content outline-none transition placeholder:text-app-content-faint focus:border-app-base-strong"
            />
            <span className="text-xs text-app-content-subtle">
              {t("admin.embed_target_hint", {
                defaultValue:
                  "CSS selector or id of the block on your site. Leave empty to render in place of the script.",
              })}
            </span>
          </label>

          <div className="overflow-hidden rounded-card border border-app-content-faint/30">
            <div className="flex items-center justify-between border-b border-app-content-faint/30 bg-app-content/5 px-4 py-2">
              <span className="font-mono text-xs text-app-content-subtle">HTML</span>
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs font-medium text-app-content-subtle transition hover:text-app-base-strong"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied
                  ? t("admin.copied", { defaultValue: "Copied" })
                  : t("admin.copy", { defaultValue: "Copy" })}
              </button>
            </div>
            <pre className="max-h-64 overflow-auto bg-white p-4 font-mono text-xs leading-relaxed text-app-content">
              {snippet}
            </pre>
          </div>

          <button
            type="button"
            onClick={handleCopy}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-button bg-app-base-strong text-sm font-semibold text-white transition hover:opacity-hover"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied
              ? t("admin.copied", { defaultValue: "Copied" })
              : t("admin.copy_code", { defaultValue: "Copy code" })}
          </button>
        </div>
      </Modal>
    </>
  )
}

export default EmbedCodeButton
